/**
 * Netzplan activity → service + schedule mapping.
 *
 * The Netzplan only encodes WHICH MONTHS an activity runs, never the weekly cadence, so
 * the user-confirmed rule applies: default to "Wöchentlich"; derive "Saisonal" (with a
 * TT.MM. window) only when the activity is absent in some months. Service names map to
 * the tenant's existing catalogue; `Liftwartung`/`Brandschutz` have no counterpart yet
 * and are flagged as missing so the wizard can offer to create them.
 */

/** Activity column (index in the Netzplan grid) → canonical activity name. */
export const ACTIVITY_COLS: Record<number, string> = {
  1: 'Winterdienst',
  2: 'Reinigung',
  3: 'Rasen',
  4: 'Strauch',
  5: 'Unkraut',
  6: 'Liftwart',
  7: 'Verglasung',
  8: 'Lampen',
  9: 'Tiefgarage',
  10: 'Laub',
  11: 'Sonderreinigung',
  12: 'Brandschutz',
};
export const NOTE_COL = 13;

export const MONTH_LABEL = [
  'Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
];

interface ActServiceCfg {
  /** Default service name (residential). */
  service: string;
  /** Service name when the object sits under the office bucket "Büroreinigung". */
  office?: string;
  /** No existing service maps here — the wizard must offer to create it. */
  missing?: boolean;
  /** Usually a free-text note rather than a scheduled task. */
  noteOnly?: boolean;
}

export const ACT_SERVICE: Record<string, ActServiceCfg> = {
  Winterdienst: { service: 'Winterdienst' },
  Reinigung: { service: 'Stiegenhausreinigung Standard', office: 'Büroreinigung Standard' },
  Rasen: { service: 'Rasenmähen Standard' },
  Strauch: { service: 'Strauch- und Heckenschnitt Standard' },
  Unkraut: { service: 'Unkrautentfernung' },
  Liftwart: { service: 'Liftwartung', missing: true },
  Verglasung: { service: 'Glasreinigung Fenster' },
  Lampen: { service: 'Lampenreinigung' },
  Tiefgarage: { service: 'Tiefgaragenreinigung einmal jährlich' },
  Laub: { service: 'Laubarbeiten' },
  Sonderreinigung: { service: 'Sondertätigkeiten', noteOnly: true },
  Brandschutz: { service: 'Brandschutz', missing: true },
};

/** Excel `Typ` labels accepted by the task import (`TaskExcelSchema.TypeLabels`). */
export type ScheduleTyp = 'Wöchentlich' | '2-wöchentlich' | 'Monatlich' | 'Saisonal';

export interface ScheduleProposal {
  typ: ScheduleTyp;
  /** `TT.MM.` window start, empty for non-seasonal. */
  von: string;
  /** `TT.MM.` window end, empty for non-seasonal. */
  bis: string;
  /** Human note explaining an uncertain inference. */
  hinweis: string;
}

const WINDOW_START = ['01.01.', '01.02.', '01.03.', '01.04.', '01.05.', '01.06.', '01.07.', '01.08.', '01.09.', '01.10.', '01.11.', '01.12.'];
const WINDOW_END = ['31.01.', '28.02.', '31.03.', '30.04.', '31.05.', '30.06.', '31.07.', '31.08.', '30.09.', '31.10.', '30.11.', '31.12.'];

export function monthsList(months: number[]): string {
  return [...months].sort((a, b) => a - b).map((i) => MONTH_LABEL[i]).join(' ');
}

/**
 * Infer a schedule proposal from the months an activity is active.
 * Default is weekly; a strict month subset becomes a seasonal window.
 */
export function inferSchedule(months: number[]): ScheduleProposal {
  const sorted = [...months].sort((a, b) => a - b);
  if (sorted.length >= 11 || sorted.length === 0) {
    return { typ: 'Wöchentlich', von: '', bis: '', hinweis: '' };
  }
  const von = sorted[0];
  const bis = sorted[sorted.length - 1];
  return {
    typ: 'Saisonal',
    von: WINDOW_START[von],
    bis: WINDOW_END[bis],
    hinweis: `aktive Monate: ${monthsList(sorted)}`,
  };
}

/** Resolve the service name for an activity, honouring the office bucket. */
export function serviceForActivity(activity: string, isOfficeBucket: boolean): string {
  const cfg = ACT_SERVICE[activity];
  if (!cfg) return activity;
  return isOfficeBucket && cfg.office ? cfg.office : cfg.service;
}

/** Leading name before the first comma (bucket children carry "Name, Straße, Ort"). */
export function stripAddr(name: string): string {
  const first = String(name).split(',')[0].trim();
  return first || String(name).trim();
}

/** Service names with no catalogue counterpart, referenced by the Netzplan. */
export const MISSING_SERVICE_NAMES = Object.values(ACT_SERVICE)
  .filter((c) => c.missing)
  .map((c) => c.service);
